import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import config from '../../../../config';
import TableBuilder from '../../../../shared/components/table/TableBuilder';
import Notify from '../../../../shared/components/widgets/Notify';
import { getExportPrefix } from '../../../../shared/components/table/functions';
import UserFormModal from './UserFormModal';

const apiUrl = config.isProdEnv ? config.serverProdUrl : config.serverDevUrl;

const UserTable = () => {
  const theme = useSelector(store => store.theme);
  const [agGrid, setAgGrid] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const changeShowModal = () => {
    setShowModal((prevState) => {
      const nextState = !prevState;
      if (!nextState) {
        setEditingUser(null);
      }
      return nextState;
    });
  };

  const openAddUser = () => {
    setEditingUser(null);
    setShowModal(true);
  };

  const openEditUser = (rowClickEvent) => {
    setEditingUser(rowClickEvent.data);
    setShowModal(true);
  };

  const parentAccess = (agGridPointer) => {
    setAgGrid(agGridPointer);
  };

  const forceRefresh = () => {
    if (agGrid) {
      agGrid.refreshData();
    }
  };

  const columnDefs = [
    { field: 'id', headerName: '#' },
    { field: 'first_name', headerName: 'First Name' },
    { field: 'last_name', headerName: 'Last Name' },
    { field: 'email', headerName: 'Email' },
    { field: 'access', headerName: 'Access' },
    { field: 'access_control', headerName: 'Access Control' },
    { field: 'password', headerName: 'Encrypted Password' },
    { field: 'temporary_password', headerName: 'Temporary Password' },
    { field: 'is_active', headerName: 'Status' },
    { field: 'theme', headerName: 'Default Theme' },
  ];

  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    editable: false,
  };

  const apiEndPoint = `${apiUrl}/users`;
  const exportPrefix = getExportPrefix('users');

  const parentDeleteRows = (selectedRows, user, endpoint, childThis) => {
    const headerWithAuth = { ...config.jsonHeader, authorization: `JWT ${user.jwtToken}` };
    const promises = [];

    selectedRows.forEach((selectedRow) => {
      const requestOptions = {
        method: 'DELETE',
        headers: headerWithAuth,
      };

      const fullDeleteUrl = `${endpoint}/${selectedRow.id}`;

      if (selectedRow.id !== '0') {
        promises.push(fetch(fullDeleteUrl, requestOptions)
          .then((results) => {
            if (!results.ok) {
              throw Error(results.statusText);
            }
            return results.json();
          }).then(() => {
            Notify.show('primary', 'Success', `User entry ${selectedRow.id} has been deleted.`);
          }).catch((error) => {
            Notify.show('danger', 'Error', `Problem deleting User entry. \n Reference: ${error}`);
          }));
      }
    });
    if (promises.length > 0) {
      Promise.all(promises).then(() => {
        childThis.refreshData();
      }, () => {
        childThis.refreshData();
      });
    }
  };

  const customButton = {
    id: 'addUserButton',
    function: () => openAddUser(),
    text: 'Add User',
  };

  return (
    <>
      <UserFormModal
        showModal={showModal}
        changeShowModal={changeShowModal}
        theme={theme}
        setForceRefresh={forceRefresh}
        editData={editingUser}
      />
      <TableBuilder
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        apiEndPoint={apiEndPoint}
        showFooter={false}
        parentDeleteRows={parentDeleteRows}
        exportPrefix={exportPrefix}
        confirmLogic
        customButton={customButton}
        parentAccess={parentAccess}
        onRowDoubleClicked={openEditUser}
      />
    </>
  );
};

export default UserTable;
